import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    TextField,
    Typography,
    Alert
} from '@mui/material';
import { IconPlus, IconTrash, IconFileText, IconDotsVertical, IconDownload, IconEdit, IconEye } from '@tabler/icons';
import api from '../../../../services/api';
import { encodePathSegments } from '../../../../../utils/media-url';

const FOLDER_COLORS = ['#1976d2', '#2e7d32', '#ed6c02', '#d32f2f', '#7b1fa2', '#455a64'];

const BACKEND_SERVER = process.env.REACT_APP_BACKEND_SERVER || 'http://127.0.0.1:8000/api/';

const formatSize = (bytes) => {
    if (!bytes) return '0 KB';
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(0)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
};

const ViewDocumentos = () => {
    const [appId, setAppId] = useState(null);
    const [folders, setFolders] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [selectedFolderId, setSelectedFolderId] = useState(null); // null = Todos

    const [createFolderOpen, setCreateFolderOpen] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [newFolderColor, setNewFolderColor] = useState(FOLDER_COLORS[0]);

    const [uploadOpen, setUploadOpen] = useState(false);
    const [uploadName, setUploadName] = useState('');
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadFolderId, setUploadFolderId] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');

    const [menuAnchor, setMenuAnchor] = useState(null);
    const [menuDocument, setMenuDocument] = useState(null);

    const [editOpen, setEditOpen] = useState(false);
    const [editDocument, setEditDocument] = useState(null);
    const [editName, setEditName] = useState('');
    const [editFolderId, setEditFolderId] = useState('');

    const [viewerOpen, setViewerOpen] = useState(false);
    const [viewerDocument, setViewerDocument] = useState(null);

    useEffect(() => {
        (async () => {
            const res = await api.get('v1/apps/default/');
            setAppId(res.data.app.id);
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        loadFolders();
    }, [appId]);

    useEffect(() => {
        if (!appId) return;
        loadDocuments();
    }, [appId, selectedFolderId]);

    const loadFolders = async () => {
        const res = await api.get(`v1/apps/${appId}/document_folders/`);
        setFolders(res.data.folders || []);
    };

    const loadDocuments = async () => {
        const query = selectedFolderId ? `?folder_id=${selectedFolderId}` : '';
        const res = await api.get(`v1/apps/${appId}/documents/${query}`);
        setDocuments(res.data.documents || []);
    };

    const handleCreateFolder = async () => {
        const name = newFolderName.trim();
        if (!name) return;
        await api.post(`v1/apps/${appId}/document_folders/`, { name, color: newFolderColor });
        setNewFolderName('');
        setCreateFolderOpen(false);
        loadFolders();
    };

    const handleDeleteFolder = async (folderId) => {
        await api.delete(`v1/apps/${appId}/document_folders/${folderId}/`);
        if (selectedFolderId === folderId) setSelectedFolderId(null);
        loadFolders();
        loadDocuments();
    };

    const openUploadDialog = () => {
        setUploadName('');
        setUploadFile(null);
        setUploadFolderId(selectedFolderId || '');
        setUploadError('');
        setUploadOpen(true);
    };

    const handleUpload = async () => {
        if (!uploadFile) return;
        setUploading(true);
        setUploadError('');
        try {
            const formData = new FormData();
            formData.append('file', uploadFile);
            formData.append('name', uploadName.trim() || uploadFile.name);
            if (uploadFolderId) formData.append('folder_id', uploadFolderId);

            await api.post(`v1/apps/${appId}/documents/`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setUploadOpen(false);
            loadDocuments();
        } catch (error) {
            setUploadError(error.response?.data?.error || 'Erro ao cadastrar o documento.');
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteDocument = async (documentId) => {
        await api.delete(`v1/apps/${appId}/documents/${documentId}/`);
        setMenuAnchor(null);
        loadDocuments();
    };

    // Mesmo endpoint de sempre (`get_image`) - sem `?download=1` ele serve
    // o PDF inline (Content-Type certo, sem forçar download), por isso dá
    // pra usar a mesma URL direto num <iframe>.
    const getMediaUrl = (document, { download } = {}) => {
        if (!document) return undefined;
        const base = `${BACKEND_SERVER}v1/apps/${appId}/media/${encodePathSegments(document.r2_key)}/`;
        if (!download) return base;
        return `${base}?download=1&filename=${encodeURIComponent(document.name || 'documento.pdf')}`;
    };

    const openViewer = (document) => {
        setViewerDocument(document);
        setMenuAnchor(null);
        setViewerOpen(true);
    };

    const openEditDialog = (document) => {
        setEditDocument(document);
        setEditName(document.name);
        setEditFolderId(document.folder || '');
        setMenuAnchor(null);
        setEditOpen(true);
    };

    const handleSaveEdit = async () => {
        await api.patch(`v1/apps/${appId}/documents/${editDocument.id}/`, {
            name: editName.trim(),
            folder_id: editFolderId || null
        });
        setEditOpen(false);
        loadDocuments();
    };

    return (
        <Box p={4} sx={{ minHeight: '100vh' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                <Box>
                    <Typography variant="h4" fontWeight="700">
                        Documentos
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        PDFs cadastrados uma vez e reutilizados nas conversas, sem precisar subir o arquivo toda vez.
                        Clique num documento para visualizá-lo direto na plataforma, sem precisar baixar.
                    </Typography>
                </Box>
                <Button variant="contained" startIcon={<IconPlus size={18} />} onClick={openUploadDialog}>
                    Novo documento
                </Button>
            </Stack>

            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={3}>
                <Chip
                    label="Todos"
                    size="small"
                    onClick={() => setSelectedFolderId(null)}
                    variant={!selectedFolderId ? 'filled' : 'outlined'}
                />
                {folders.map((folder) => (
                    <Chip
                        key={folder.id}
                        label={folder.name}
                        size="small"
                        onClick={() => setSelectedFolderId(folder.id)}
                        onDelete={() => handleDeleteFolder(folder.id)}
                        deleteIcon={<IconTrash size={14} />}
                        variant={selectedFolderId === folder.id ? 'filled' : 'outlined'}
                        sx={{
                            backgroundColor: selectedFolderId === folder.id ? folder.color || undefined : undefined,
                            borderColor: folder.color || undefined,
                            color: selectedFolderId === folder.id ? '#fff' : undefined
                        }}
                    />
                ))}
                <Chip label="+ Nova pasta" size="small" variant="outlined" onClick={() => setCreateFolderOpen(true)} />
            </Box>

            <Grid container spacing={2}>
                {documents.map((doc) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={doc.id}>
                        <Card sx={{ borderRadius: 3 }}>
                            <CardContent onClick={() => openViewer(doc)} sx={{ cursor: 'pointer' }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                    <Box display="flex" gap={1} alignItems="center" sx={{ overflow: 'hidden' }}>
                                        <IconFileText size={28} color="#d32f2f" />
                                        <Typography variant="subtitle2" noWrap title={doc.name}>
                                            {doc.name}
                                        </Typography>
                                    </Box>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setMenuAnchor(e.currentTarget);
                                            setMenuDocument(doc);
                                        }}
                                    >
                                        <IconDotsVertical size={18} />
                                    </IconButton>
                                </Stack>
                                <Typography variant="caption" color="text.secondary">
                                    {formatSize(doc.size)}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
                {documents.length === 0 && (
                    <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                            Nenhum documento cadastrado{selectedFolderId ? ' nessa pasta' : ''}.
                        </Typography>
                    </Grid>
                )}
            </Grid>

            <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
                <MenuItem onClick={() => openViewer(menuDocument)}>
                    <IconEye size={16} style={{ marginRight: 8 }} /> Visualizar
                </MenuItem>
                <MenuItem
                    component="a"
                    href={getMediaUrl(menuDocument, { download: true })}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setMenuAnchor(null)}
                >
                    <IconDownload size={16} style={{ marginRight: 8 }} /> Baixar
                </MenuItem>
                <MenuItem onClick={() => openEditDialog(menuDocument)}>
                    <IconEdit size={16} style={{ marginRight: 8 }} /> Renomear / mover
                </MenuItem>
                <MenuItem onClick={() => handleDeleteDocument(menuDocument.id)} sx={{ color: 'error.main' }}>
                    <IconTrash size={16} style={{ marginRight: 8 }} /> Excluir
                </MenuItem>
            </Menu>

            {/* Dialog: visualizar documento */}
            <Dialog
                open={viewerOpen}
                onClose={() => setViewerOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { height: '90vh' } }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle1" fontWeight={700} noWrap title={viewerDocument?.name}>
                        {viewerDocument?.name}
                    </Typography>
                    <Button
                        size="small"
                        startIcon={<IconDownload size={16} />}
                        component="a"
                        href={getMediaUrl(viewerDocument, { download: true })}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Baixar
                    </Button>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    {viewerDocument && (
                        <Box
                            component="iframe"
                            src={getMediaUrl(viewerDocument)}
                            title={viewerDocument.name}
                            sx={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
                        />
                    )}
                </DialogContent>
            </Dialog>

            {/* Dialog: novo documento */}
            <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Novo documento</DialogTitle>
                <DialogContent>
                    {uploadError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {uploadError}
                        </Alert>
                    )}
                    <TextField
                        fullWidth
                        label="Nome de exibição"
                        placeholder="ex: Catálogo 2026"
                        value={uploadName}
                        onChange={(e) => setUploadName(e.target.value)}
                        sx={{ mt: 1, mb: 2 }}
                    />
                    <TextField
                        select
                        fullWidth
                        label="Pasta (opcional)"
                        value={uploadFolderId}
                        onChange={(e) => setUploadFolderId(e.target.value)}
                        sx={{ mb: 2 }}
                    >
                        <MenuItem value="">Sem pasta</MenuItem>
                        {folders.map((folder) => (
                            <MenuItem key={folder.id} value={folder.id}>
                                {folder.name}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Button variant="outlined" component="label" fullWidth>
                        {uploadFile ? uploadFile.name : 'Selecionar PDF'}
                        <input
                            type="file"
                            hidden
                            accept="application/pdf"
                            onChange={(e) => setUploadFile(e.target.files[0] || null)}
                        />
                    </Button>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUploadOpen(false)}>Cancelar</Button>
                    <Button variant="contained" disabled={!uploadFile || uploading} onClick={handleUpload}>
                        Cadastrar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog: nova pasta */}
            <Dialog open={createFolderOpen} onClose={() => setCreateFolderOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Nova pasta</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Nome da pasta"
                        value={newFolderName}
                        onChange={(e) => setNewFolderName(e.target.value)}
                        sx={{ mt: 1, mb: 2 }}
                    />
                    <Typography variant="body2" color="text.secondary" mb={1}>
                        Cor
                    </Typography>
                    <Box display="flex" gap={1}>
                        {FOLDER_COLORS.map((color) => (
                            <Box
                                key={color}
                                onClick={() => setNewFolderColor(color)}
                                sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: '50%',
                                    backgroundColor: color,
                                    cursor: 'pointer',
                                    border: newFolderColor === color ? '2px solid #000' : '2px solid transparent'
                                }}
                            />
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateFolderOpen(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleCreateFolder} disabled={!newFolderName.trim()}>
                        Criar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog: renomear/mover documento */}
            <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Renomear / mover documento</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Nome de exibição"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        sx={{ mt: 1, mb: 2 }}
                    />
                    <TextField select fullWidth label="Pasta" value={editFolderId} onChange={(e) => setEditFolderId(e.target.value)}>
                        <MenuItem value="">Sem pasta</MenuItem>
                        {folders.map((folder) => (
                            <MenuItem key={folder.id} value={folder.id}>
                                {folder.name}
                            </MenuItem>
                        ))}
                    </TextField>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditOpen(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleSaveEdit} disabled={!editName.trim()}>
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ViewDocumentos;
